import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// SEO configuration for different pages
const SEO_CONFIG = {
  '/': {
    title: 'Find den bedste daginstitution for din familie - Vuggestueguiden & Børnehaveguiden',
    description: 'Sammenlign kvalitet, trivsel og pædagogik på tværs af daginstitutioner i Danmark. Vi analyserer officielle tilsynsrapporter, så du kan træffe et trygt valg for dit barn.',
    keywords: 'daginstitution, vuggestue, børnehave, sammenligning, tilsyn, kvalitet, Danmark, børn, pædagogik',
    type: 'website'
  },
  '/map': {
    title: 'Kort over daginstitutioner - Find institutioner nær dig',
    description: 'Udforsk daginstitutioner på et interaktivt kort. Find vuggestuer og børnehaver i dit område og sammenlign deres kvalitet baseret på officielle tilsynsrapporter.',
    keywords: 'daginstitution kort, vuggestue kort, børnehave kort, find daginstitution, lokale institutioner',
    type: 'website'
  },
  '/institution-list': {
    title: 'Liste over daginstitutioner - Sammenlign og filtrer',
    description: 'Gennemse en komplet liste over daginstitutioner i Danmark. Filtrer efter type, lokation og kvalitetsscorer baseret på officielle tilsynsrapporter.',
    keywords: 'daginstitution liste, vuggestue liste, børnehave liste, institution oversigt, filtrer institutioner',
    type: 'website'
  },
  '/compare': {
    title: 'Sammenlign daginstitutioner - Side om side sammenligning',
    description: 'Sammenlign dine valgte daginstitutioner side om side. Se detaljerede scoringer for trivsel, pædagogik og kvalitet baseret på officielle data.',
    keywords: 'sammenlign daginstitutioner, institution sammenligning, kvalitetssammenligning, vuggestue vs børnehave',
    type: 'website'
  },
  '/login': {
    title: 'Log ind - Vuggestueguiden & Børnehaveguiden',
    description: 'Log ind for at gemme dine favorit institutioner og få adgang til avancerede sammenligningsværktøjer.',
    keywords: 'log ind, bruger konto, daginstitution konto',
    type: 'website',
    robots: 'noindex, nofollow'
  },
  '/register': {
    title: 'Opret konto - Vuggestueguiden & Børnehaveguiden',
    description: 'Opret en basis konto for at gemme dine favoritter og få det bedste ud af vores sammenligningsværktøjer.',
    keywords: 'opret konto, registrer, basis konto, daginstitution',
    type: 'website',
    robots: 'noindex, nofollow'
  }
};

// Function to determine domain-specific branding
function getDomainBranding() {
  const hostname = window.location.hostname;
  if (hostname.includes('vuggestueguiden')) {
    return {
      siteName: 'Vuggestueguiden.dk',
      focus: 'vuggestuer',
      ageGroup: '0-3 år'
    };
  } else if (hostname.includes('bornehaveguiden')) {
    return {
      siteName: 'Børnehaveguiden.dk',
      focus: 'børnehaver', 
      ageGroup: '3-6 år'
    };
  }
  return {
    siteName: 'Daginstitutions Overblik',
    focus: 'daginstitutioner',
    ageGroup: '0-6 år'
  };
}

// Function to determine the canonical domain and URL
function getCanonicalUrl(pathname) {
  // Always use vuggestueguiden.dk as the primary canonical domain
  const canonicalDomain = 'https://vuggestueguiden.dk';
  return canonicalDomain + pathname;
}

// Function to update meta tags
function updateMetaTags(config, branding, pathname) {
  //const currentUrl = window.location.href;
  const canonicalUrl = getCanonicalUrl(pathname);
  
  // Update title
  document.title = config.title;
  
  // Update or create meta tags
  const metaTags = [
    { name: 'description', content: config.description },
    { name: 'keywords', content: config.keywords },
    { name: 'author', content: branding.siteName },
    { name: 'robots', content: config.robots || 'index, follow' },
    
    // Open Graph tags
    { property: 'og:title', content: config.title },
    { property: 'og:description', content: config.description },
    { property: 'og:type', content: config.type },
    { property: 'og:url', content: canonicalUrl },
    { property: 'og:site_name', content: 'Vuggestueguiden.dk - Daginstitutions Overblik' },
    { property: 'og:locale', content: 'da_DK' },
    { property: 'og:image', content: 'https://sadaycaresolution.blob.core.windows.net/media/images/og/silhouette-kids.png' },
    { property: 'og:image:width', content: '1200' },
    { property: 'og:image:height', content: '630' },
    { property: 'og:image:alt', content: 'Børn i silhuet - Find den bedste daginstitution for dit barn' },
    
    // Twitter Card tags
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: config.title },
    { name: 'twitter:description', content: config.description },
    { name: 'twitter:image', content: 'https://sadaycaresolution.blob.core.windows.net/media/images/og/silhouette-kids.png' },
    
    // Additional SEO tags
    { name: 'theme-color', content: '#2563eb' },
    { name: 'apple-mobile-web-app-capable', content: 'yes' },
    { name: 'apple-mobile-web-app-status-bar-style', content: 'default' },
    { name: 'apple-mobile-web-app-title', content: branding.siteName },
    
    // Geo tags for local SEO
    { name: 'geo.region', content: 'DK' },
    { name: 'geo.country', content: 'Denmark' },
    { name: 'ICBM', content: '55.6761, 12.5683' }, // Copenhagen coordinates
    
    // Business/Service specific
    { name: 'classification', content: 'Education, Childcare, Comparison Service' },
    { name: 'category', content: 'Education' },
    { name: 'coverage', content: 'Denmark' },
    { name: 'distribution', content: 'Global' },
    { name: 'rating', content: 'General' }
  ];

  metaTags.forEach(({ name, property, content }) => {
    const selector = name ? `meta[name="${name}"]` : `meta[property="${property}"]`;
    let metaTag = document.querySelector(selector);
    
    if (metaTag) {
      metaTag.setAttribute('content', content);
    } else {
      metaTag = document.createElement('meta');
      if (name) metaTag.setAttribute('name', name);
      if (property) metaTag.setAttribute('property', property);
      metaTag.setAttribute('content', content);
      document.head.appendChild(metaTag);
    }
  });

  // Update canonical URL - always point to primary domain
  let canonical = document.querySelector('link[rel="canonical"]');
  if (canonical) {
    canonical.setAttribute('href', canonicalUrl);
  } else {
    canonical = document.createElement('link');
    canonical.setAttribute('rel', 'canonical');
    canonical.setAttribute('href', canonicalUrl);
    document.head.appendChild(canonical);
  }

  // Update alternate language links - point to canonical domain
  let alternateDa = document.querySelector('link[rel="alternate"][hreflang="da"]');
  if (alternateDa) {
    alternateDa.setAttribute('href', canonicalUrl);
  } else {
    alternateDa = document.createElement('link');
    alternateDa.setAttribute('rel', 'alternate');
    alternateDa.setAttribute('hreflang', 'da');
    alternateDa.setAttribute('href', canonicalUrl);
    document.head.appendChild(alternateDa);
  }

  // Add alternate domain links to indicate domain relationships
  const currentHostname = window.location.hostname;
  if (currentHostname.includes('bornehaveguiden')) {
    // Add link to indicate the primary domain version
    let alternatePrimary = document.querySelector('link[rel="alternate"][data-domain="primary"]');
    if (alternatePrimary) {
      alternatePrimary.setAttribute('href', canonicalUrl);
    } else {
      alternatePrimary = document.createElement('link');
      alternatePrimary.setAttribute('rel', 'alternate');
      alternatePrimary.setAttribute('data-domain', 'primary');
      alternatePrimary.setAttribute('href', canonicalUrl);
      document.head.appendChild(alternatePrimary);
    }
  }

  // Update structured data - but skip for institution pages since InstitutionStructuredData handles that
  if (!pathname.startsWith('/institution/')) {
    updateStructuredData(config, branding, canonicalUrl);
  }
}

// Function to determine page type and structured data dynamically
function getPageTypeAndStructuredData(pathname, config, canonicalUrl) {
  const canonicalDomain = 'https://vuggestueguiden.dk';
  
  // Base structured data that applies to all pages
  const baseStructuredData = {
    '@context': 'https://schema.org',
    name: config.title,
    url: canonicalUrl,
    description: config.description,
    inLanguage: 'da',
    publisher: {
      '@type': 'Organization',
      name: 'Vuggestueguiden.dk - Daginstitutions Overblik',
      url: canonicalDomain,
      logo: {
        '@type': 'ImageObject',
        url: `${canonicalDomain}/logo512.png`
      }
    }
  };

  // Determine page type and specific structured data based on pathname
  if (pathname === '/') {
    // Home page - WebSite with search action
    return {
      ...baseStructuredData,
      '@type': 'WebSite',
      potentialAction: {
        '@type': 'SearchAction',
        target: {
          '@type': 'EntryPoint',
          urlTemplate: `${canonicalDomain}/map?search={search_term_string}`
        },
        'query-input': 'required name=search_term_string'
      }
    };
  } else if (pathname.startsWith('/institution/')) {
    // Institution detail pages - this case should never be reached since we skip structured data generation
    // for institution pages in updateMetaTags. This is kept for completeness.
    return {
      ...baseStructuredData,
      '@type': 'WebPage'
    };
  } else if (pathname === '/map') {
    // Map page
    return {
      ...baseStructuredData,
      '@type': 'WebPage',
      mainEntity: {
        '@type': 'Map',
        name: 'Daginstitutioner kort',
        description: 'Interaktivt kort over daginstitutioner i Danmark'
      }
    };
  } else if (pathname === '/compare') {
    // Comparison page
    return {
      ...baseStructuredData,
      '@type': 'WebPage',
      mainEntity: {
        '@type': 'CompareAction',
        name: 'Sammenlign daginstitutioner',
        description: 'Sammenlign kvalitet og services på tværs af daginstitutioner'
      }
    };
  } else if (pathname === '/institution-list') {
    // Institution list page
    return {
      ...baseStructuredData,
      '@type': 'CollectionPage',
      mainEntity: {
        '@type': 'ItemList',
        name: 'Liste over daginstitutioner',
        description: 'Komplet liste over daginstitutioner i Danmark'
      }
    };
  } else if (pathname.match(/^\/(login|register|payment-success|payment-cancel)/)) {
    // Utility pages that shouldn't be indexed
    return {
      ...baseStructuredData,
      '@type': 'WebPage'
    };
  } else {
    // Default for any other pages (future pages)
    return {
      ...baseStructuredData,
      '@type': 'WebPage'
    };
  }
}

// Function to add structured data
function updateStructuredData(config, branding, canonicalUrl) {
  // Get dynamic structured data based on current pathname
  const pathname = window.location.pathname;
  const structuredData = getPageTypeAndStructuredData(pathname, config, canonicalUrl);

  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.setAttribute('data-type', 'seo-head-structured-data');
  script.textContent = JSON.stringify(structuredData);
  document.head.appendChild(script);
}

// Function to generate dynamic SEO config for pages not explicitly defined
function generateDynamicSEOConfig(pathname, branding) {
  // Institution detail pages
  if (pathname.startsWith('/institution/')) {
    const institutionSlug = pathname.replace('/institution/', '');
    
    // Convert slug back to readable institution name
    // Handle Danish characters properly
    let institutionName = institutionSlug
      .replace(/-/g, ' ')
      .replace(/ae/g, 'æ')
      .replace(/oe/g, 'ø')
      .replace(/aa/g, 'å')
      .replace(/\b\w/g, l => l.toUpperCase());
    
    return {
      title: `${institutionName} - Daginstitution detaljer og kvalitetsvurdering`,
      description: `Se detaljeret information om ${institutionName}. Læs om kvalitet, trivsel og pædagogik baseret på officielle tilsynsrapporter.`,
      keywords: `${institutionName}, daginstitution, ${branding.focus}, kvalitet, tilsynsrapport, ${branding.ageGroup}`,
      type: 'article'
    };
  }
  
  // Future dynamic pages - you can add more patterns here
  if (pathname.startsWith('/search/')) {
    const searchTerm = pathname.replace('/search/', '').replace(/-/g, ' ');
    return {
      title: `Søgeresultater for "${searchTerm}" - ${branding.siteName}`,
      description: `Find daginstitutioner relateret til "${searchTerm}". Sammenlign kvalitet og services baseret på officielle data.`,
      keywords: `søg, ${searchTerm}, daginstitution, ${branding.focus}, find institution`,
      type: 'website'
    };
  }
  
  if (pathname.startsWith('/area/')) {
    const areaName = pathname.replace('/area/', '').replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    return {
      title: `Daginstitutioner i ${areaName} - ${branding.siteName}`,
      description: `Find og sammenlign daginstitutioner i ${areaName}. Se kvalitetsvurderinger og vælg den bedste institution for dit barn.`,
      keywords: `${areaName}, daginstitution, lokale institutioner, ${branding.focus}, område`,
      type: 'website'
    };
  }
  
  // Default fallback for any unrecognized pages
  return {
    title: `${branding.siteName} - Find den bedste daginstitution`,
    description: `Sammenlign kvalitet, trivsel og pædagogik på tværs af ${branding.focus} i Danmark. Træf et trygt valg baseret på officielle data.`,
    keywords: `daginstitution, ${branding.focus}, sammenligning, kvalitet, Danmark, ${branding.ageGroup}`,
    type: 'website'
  };
}

// Main SEO component
export default function SEOHead() {
  const location = useLocation();
  
  useEffect(() => {
    const pathname = location.pathname;
    const branding = getDomainBranding();
    
    // ALWAYS remove existing SEOHead structured data first, regardless of page type
    const existingScript = document.querySelector('script[data-type="seo-head-structured-data"]');
    if (existingScript) {
      existingScript.remove();
    }
    
    // Get SEO config - use predefined config or generate dynamic one
    const config = SEO_CONFIG[pathname] || generateDynamicSEOConfig(pathname, branding);
    
    // Only call updateMetaTags once - remove the duplicate call
    updateMetaTags(config, branding, pathname);
    
  }, [location.pathname]);

  return null; // This component doesn't render anything
}

export { SEO_CONFIG, getDomainBranding, generateDynamicSEOConfig };
